import { Badge, Body } from '@e2n/ui/v2';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { useVisibleQualificationCount } from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/components/RotationsplanCard/useVisibleQualificationCount';
import { QualifikationBadge } from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/components/RotationsplanCard/QualifikationBadge';
import { qualifikationBadgeClassName } from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/components/RotationsplanCard/qualifikationBadgeClassName';
import { faFileCertificate } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

export function Qualifikationen({
  rotationsplan,
  cardElement,
}: {
  rotationsplan: Rotationsplan;
  cardElement: HTMLDivElement | null;
}) {
  const { t } = useTranslation();
  const arbeitsbereichNames = rotationsplan.abgeschlosseneStationen
    .filter((station) => station instanceof ArbeitsbereichStation)
    .map((station) => station.arbeitsbereich.name);
  const uniqueNames = [...new Set(arbeitsbereichNames)];
  const visibility = useVisibleQualificationCount(uniqueNames);
  const remainingCount = uniqueNames.length - visibility.visibleCount;
  const badges = uniqueNames.map((name) => (
    <Badge key={name} className={qualifikationBadgeClassName}>
      <span className="truncate">
        {name.length > 20 ? `${name.slice(0, 19)}…` : name}
      </span>
    </Badge>
  ));

  return (
    <div className="flex gap-1 items-start min-h-5.5 min-w-0">
      <FontAwesomeIcon
        icon={faFileCertificate}
        className="text-labels-secondary shrink-0 h-5.5"
      />
      {uniqueNames.length < 1 ? (
        <Body className="text-labels-secondary leading-6" size="sm">
          {t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.keineQualifikation',
          )}
        </Body>
      ) : (
        <div className="relative min-w-0 flex-1">
          <div
            ref={visibility.measurementRef}
            aria-hidden="true"
            className="absolute inset-x-0 top-0 invisible pointer-events-none flex gap-1 overflow-hidden">
            {badges}
            <Badge className={qualifikationBadgeClassName}>
              +{uniqueNames.length}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1">
            {uniqueNames.slice(0, visibility.visibleCount).map((name) => (
              <QualifikationBadge
                key={name}
                cardElement={cardElement}
                names={[name]}
                label={name.length > 20 ? `${name.slice(0, 19)}…` : name}
              />
            ))}
            {remainingCount > 0 && (
              <QualifikationBadge
                cardElement={cardElement}
                names={uniqueNames.slice(visibility.visibleCount)}
                label={`+${remainingCount}`}
                isRemaining
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
